import { motion, useScroll, useTransform } from "framer-motion";
import { useBrand } from "@/brand";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.14 },
  }),
};

const HeroSection = () => {
  const { claimWord } = useBrand();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="inicio" ref={ref} className="screen relative pt-20 pb-10 grid-lines" data-testid="hero-section">
      <div className="noise-overlay" />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
        <motion.div style={{ y: yText }} className="lg:col-span-7 relative z-10">
          <h1 className="font-serif-display font-light tracking-tighter text-5xl sm:text-6xl lg:text-[5.2rem] leading-[0.98]" data-testid="hero-title">
            <span className="hero-line-mask">
              <motion.span className="block" custom={0} initial="hidden" animate="visible" variants={lineVariant}>
                La defensa,
              </motion.span>
            </span>
            <span className="hero-line-mask">
              <motion.span className="block italic text-[var(--gm-accent)]" custom={1} initial="hidden" animate="visible" variants={lineVariant}>
                {claimWord}
              </motion.span>
            </span>
            <span className="hero-line-mask">
              <motion.span className="block" custom={2} initial="hidden" animate="visible" variants={lineVariant}>
                desde el primer día.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-7 max-w-xl text-base md:text-lg font-light text-[var(--gm-muted)] leading-relaxed"
            data-testid="hero-subtitle"
          >
            Más de 20 años litigando y una trayectoria en el Poder Judicial. Conocemos
            el criterio del juzgador porque estuvimos del otro lado del expediente.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="mt-8 flex flex-wrap items-center gap-5"
          >
            <a href="#contacto" className="btn-navy px-8 py-4 rounded-full text-sm tracking-wide" data-testid="hero-cta-consulta">
              Agendar consulta
            </a>
            <a href="#plataforma" className="btn-outline-navy px-8 py-4 rounded-full text-sm tracking-wide" data-testid="hero-cta-portal">
              Acceder a la plataforma
            </a>
          </motion.div>

          <motion.svg
            width="220"
            height="70"
            viewBox="0 0 220 70"
            fill="none"
            className="mt-14"
            data-testid="hero-signature"
          >
            <motion.path
              d="M10 50 C 25 10, 40 10, 42 40 C 44 58, 30 60, 28 48 C 26 36, 48 22, 62 26 C 76 30, 66 52, 80 44 C 90 38, 96 24, 108 30 C 118 35, 114 48, 126 40 C 138 32, 146 26, 160 32 C 172 37, 180 30, 194 24 M 60 58 C 100 52, 150 54, 205 46"
              stroke="var(--gm-ink)"
              strokeWidth="1.6"
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ delay: 1.7, duration: 2, ease: "easeInOut" }}
            />
            <motion.text
              x="10"
              y="68"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 3.2, duration: 1 }}
              className="font-serif-display"
              fontSize="10"
              letterSpacing="3"
              fill="var(--gm-muted)"
            >
              ABOG. GUILLERMO MORALES RODRÍGUEZ
            </motion.text>
          </motion.svg>
        </motion.div>

        <motion.div style={{ y: yImg }} className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, clipPath: "inset(12% 12% 12% 12%)" }}
            animate={{ opacity: 1, scale: 1, clipPath: "inset(0% 0% 0% 0%)" }}
            transition={{ delay: 0.9, duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="img-frame rounded-sm shadow-[0_40px_80px_rgba(11,22,44,0.18)]"
          >
            <img
              src="/images/hero.jpg"
              alt="Oficina del despacho"
              className="w-full h-[520px] object-cover"
              data-testid="hero-image"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.8, duration: 0.9 }}
            className="absolute -bottom-8 -left-8 backdrop-blur-xl bg-[var(--gm-surface-70)] border border-[var(--gm-vidrio)] shadow-sm rounded-sm px-8 py-6"
            data-testid="hero-stat-card"
          >
            <p className="font-serif-display text-5xl font-medium">+20</p>
            <p className="text-xs tracking-[0.2em] uppercase text-[var(--gm-muted)] mt-1">años de experiencia</p>
          </motion.div>
        </motion.div>
      </div>

      <motion.a
        href="#practica"
        aria-label="Ir a la siguiente sección"
        data-testid="next-arrow-practica"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[var(--gm-muted)] transition-colors duration-300 hover:text-[var(--gm-accent)]"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">Desliza</span>
        <ArrowDown className="w-4 h-4 animate-bounce motion-reduce:animate-none" strokeWidth={1.5} />
      </motion.a>
</section>
  );
};

export default HeroSection;
